import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Switch } from '../components/ui/switch';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import { Plus, Search, MoreHorizontal, Eye, Edit, Trash2, UserCog, AlertTriangle, CheckCircle, Filter } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const Vendors = () => {
  const { getAuthHeaders } = useAuth();
  const navigate = useNavigate();
  const [vendors, setVendors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all'); // 'all', 'active', 'inactive'

  const fetchVendors = async () => {
    try {
      const params = new URLSearchParams();
      if (search) params.append('search', search);
      if (statusFilter !== 'all') params.append('status', statusFilter);
      
      const response = await fetch(`${API_URL}/api/vendors?${params}`, {
        headers: getAuthHeaders(),
        credentials: 'include'
      });
      if (response.ok) {
        const data = await response.json();
        setVendors(data);
      }
    } catch (error) {
      toast.error('Failed to load vendors');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchVendors();
  }, [search, statusFilter]);

  const handleToggleStatus = async (vendorId, currentStatus) => {
    const newStatus = !currentStatus;
    try {
      const response = await fetch(`${API_URL}/api/vendors/${vendorId}/status`, {
        method: 'PATCH',
        headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ is_active: newStatus })
      });
      
      if (response.ok) {
        toast.success(`Vendor ${newStatus ? 'activated' : 'deactivated'}`);
        // Update local state
        setVendors(prev => prev.map(v => 
          v.vendor_id === vendorId ? { ...v, is_active: newStatus } : v
        ));
      } else {
        toast.error('Failed to update vendor status');
      }
    } catch (error) {
      toast.error('Failed to update vendor status');
    }
  };

  const handleDelete = async (vendorId) => {
    if (!window.confirm('Are you sure you want to delete this vendor?')) return;
    
    try {
      const response = await fetch(`${API_URL}/api/vendors/${vendorId}`, {
        method: 'DELETE',
        headers: getAuthHeaders(),
        credentials: 'include'
      });
      if (response.ok) {
        toast.success('Vendor deleted');
        fetchVendors();
      }
    } catch (error) {
      toast.error('Failed to delete vendor');
    }
  };

  return (
    <div className="space-y-6" data-testid="vendors-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row gap-4 justify-between">
        <div className="flex gap-3 flex-1">
          <div className="relative flex-1 max-w-md">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Search vendors..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-10"
              data-testid="vendor-search"
            />
          </div>
          {/* Status Filter */}
          <div className="flex items-center gap-1 bg-muted rounded-lg p-1">
            <Button
              variant={statusFilter === 'all' ? 'default' : 'ghost'}
              size="sm"
              onClick={() => setStatusFilter('all')}
              className="text-xs"
            >
              All
            </Button>
            <Button
              variant={statusFilter === 'active' ? 'default' : 'ghost'}
              size="sm"
              onClick={() => setStatusFilter('active')}
              className="text-xs"
            >
              Active
            </Button>
            <Button
              variant={statusFilter === 'inactive' ? 'default' : 'ghost'}
              size="sm"
              onClick={() => setStatusFilter('inactive')}
              className="text-xs"
            >
              Inactive
            </Button>
          </div>
        </div>
        <Link to="/vendors/new">
          <Button className="gap-2" data-testid="add-vendor-btn">
            <Plus className="h-4 w-4" />
            Add Vendor
          </Button>
        </Link>
      </div>

      {/* Table */}
      <Card>
        <CardContent className="p-0">
          {loading ? (
            <div className="flex items-center justify-center h-64">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
            </div>
          ) : vendors.length === 0 ? (
            <div className="text-center py-12">
              <UserCog className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
              <h3 className="text-lg font-semibold mb-2">No vendors found</h3>
              <p className="text-muted-foreground mb-4">Get started by adding your first vendor</p>
              <Link to="/vendors/new">
                <Button>Add Vendor</Button>
              </Link>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-24">#</TableHead>
                  <TableHead>Name</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Type</TableHead>
                  <TableHead>Language Pairs</TableHead>
                  <TableHead>Specialties</TableHead>
                  <TableHead>Compliance</TableHead>
                  <TableHead>Reliability</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {vendors.map((vendor) => (
                  <TableRow key={vendor.vendor_id} data-testid={`vendor-row-${vendor.vendor_id}`}>
                    <TableCell 
                      className="font-mono text-sm text-blue-600 hover:text-blue-800 hover:underline cursor-pointer"
                      onClick={() => navigate(`/vendors/${vendor.vendor_id}`)}
                    >
                      {vendor.vendor_number || '-'}
                    </TableCell>
                    <TableCell>
                      <div>
                        <div 
                          className="font-medium text-blue-600 hover:text-blue-800 hover:underline cursor-pointer"
                          onClick={() => navigate(`/vendors/${vendor.vendor_id}`)}
                        >
                          {vendor.last_name}, {vendor.first_name}
                          {vendor.business_name && (
                            <span className="text-muted-foreground font-normal ml-2">({vendor.business_name})</span>
                          )}
                        </div>
                        <div className="text-sm text-muted-foreground">{vendor.email}</div>
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <Switch
                          checked={vendor.is_active !== false}
                          onCheckedChange={() => handleToggleStatus(vendor.vendor_id, vendor.is_active !== false)}
                          data-testid={`vendor-status-toggle-${vendor.vendor_id}`}
                        />
                        <Badge 
                          variant={vendor.is_active !== false ? "default" : "secondary"}
                          className={vendor.is_active !== false ? "bg-green-100 text-green-800" : "bg-gray-100 text-gray-600"}
                        >
                          {vendor.is_active !== false ? 'Active' : 'Inactive'}
                        </Badge>
                      </div>
                    </TableCell>
                    <TableCell>
                      <Badge variant={
                        vendor.vendor_type === 'interpreter' ? 'default' : 
                        vendor.vendor_type === 'translator' ? 'secondary' : 'outline'
                      } className="text-xs">
                        {vendor.vendor_type === 'both' ? 'Both' : 
                         vendor.vendor_type === 'interpreter' ? 'Interpreter' : 
                         vendor.vendor_type === 'translator' ? 'Translator' : 'Both'}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <div className="flex flex-wrap gap-1 max-w-[200px]">
                        {vendor.language_pairs?.length > 0 ? (
                          <>
                            {vendor.language_pairs.slice(0, 2).map((pair, idx) => (
                              <Badge key={idx} variant="secondary" className="text-xs">
                                {pair.source_language?.toUpperCase()} ↔ {pair.target_language?.toUpperCase()}
                              </Badge>
                            ))}
                            {vendor.language_pairs.length > 2 && (
                              <Badge variant="outline" className="text-xs">
                                +{vendor.language_pairs.length - 2}
                              </Badge>
                            )}
                          </>
                        ) : vendor.languages?.length > 0 ? (
                          <>
                            {vendor.languages.slice(0, 3).map((lang) => (
                              <Badge key={lang} variant="outline" className="text-xs">
                                {lang.toUpperCase()}
                              </Badge>
                            ))}
                            {vendor.languages.length > 3 && (
                              <Badge variant="outline" className="text-xs">
                                +{vendor.languages.length - 3}
                              </Badge>
                            )}
                          </>
                        ) : (
                          <span className="text-xs text-muted-foreground">-</span>
                        )}
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="flex flex-wrap gap-1">
                        {vendor.specialties?.slice(0, 2).map((spec) => (
                          <Badge key={spec} variant="outline" className="text-xs capitalize">
                            {spec}
                          </Badge>
                        ))}
                      </div>
                    </TableCell>
                    <TableCell>
                      {vendor.w9_on_file ? (
                        <div className="flex items-center gap-1 text-green-600">
                          <CheckCircle className="h-4 w-4" />
                          <span className="text-xs">Compliant</span>
                        </div>
                      ) : (
                        <div className="flex items-center gap-1 text-orange-500">
                          <AlertTriangle className="h-4 w-4" />
                          <span className="text-xs">W-9 Missing</span>
                        </div>
                      )}
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <span className={`font-medium ${
                          vendor.reliability_score >= 95 ? 'text-green-600' :
                          vendor.reliability_score >= 85 ? 'text-yellow-600' : 'text-red-600'
                        }`}>
                          {vendor.reliability_score?.toFixed(1)}%
                        </span>
                        <span className="text-xs text-muted-foreground">
                          ({vendor.total_assignments || 0} jobs)
                        </span>
                      </div>
                    </TableCell>
                    <TableCell className="text-right">
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="icon" data-testid={`vendor-actions-${vendor.vendor_id}`}>
                            <MoreHorizontal className="h-4 w-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem onClick={() => navigate(`/vendors/${vendor.vendor_id}`)}>
                            <Eye className="h-4 w-4 mr-2" /> View
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={() => navigate(`/vendors/${vendor.vendor_id}/edit`)}>
                            <Edit className="h-4 w-4 mr-2" /> Edit
                          </DropdownMenuItem>
                          <DropdownMenuItem 
                            onClick={() => handleDelete(vendor.vendor_id)}
                            className="text-destructive"
                          >
                            <Trash2 className="h-4 w-4 mr-2" /> Delete
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default Vendors;
